import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Database, Upload, RotateCcw, RefreshCw, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("pricelens_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const CalibrationCard = ({ isAdmin, onCalibrationChange }) => {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/calibration/status`);
      setStatus(data);
    } catch (e) {
      console.error("Failed to fetch calibration status:", e);
    }
  };

  const handleFile = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      selected.forEach((f) => form.append("files", f));
      const { data } = await axios.post(`${API}/calibration/upload`, form, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `${data.message}${data.category_curves.length ? ` — per-category curves: ${data.category_curves.join(", ")}` : ""}`
      );
      fetchStatus();
      onCalibrationChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleReset = async () => {
    try {
      await axios.delete(`${API}/calibration`, { headers: getAuthHeaders() });
      toast.success("Reverted to default calibration");
      fetchStatus();
      onCalibrationChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  };

  if (!status) return null;
  const isCustom = status.source === "custom";

  return (
    <div
      data-testid="calibration-card"
      className="bg-white rounded-xl border border-[#e0dbd2] px-5 py-4 flex flex-wrap items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
          <Database className="h-4.5 w-4.5 text-emerald-600" />
        </div>
        <div>
          <p className="text-sm font-extrabold text-[#1a1814] flex items-center gap-2">
            Sales Calibration
            {isCustom && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> Custom
              </span>
            )}
          </p>
          <p data-testid="calibration-status-text" className="text-xs text-[#8a8278]">
            {status.description}
            {isCustom && status.category_curves?.length > 0 && (
              <> · Per-category: {status.category_curves.join(", ")}</>
            )}
          </p>
        </div>
      </div>

      {isAdmin && (
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            multiple
            className="hidden"
            data-testid="calibration-file-input"
            onChange={handleFile}
          />
          <Button
            data-testid="calibration-upload-btn"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-[#1a1814] hover:bg-[#2a2824] text-white font-bold"
          >
            {uploading ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading...
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Dataset
              </>
            )}
          </Button>
          {isCustom && (
            <Button
              data-testid="calibration-reset-btn"
              size="sm"
              variant="outline"
              onClick={handleReset}
              className="border-[#e0dbd2]"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
